import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Button,
  Grid,
  Snackbar,
  TextField,
  Typography,
} from '@mui/material';
import Alert from '@mui/material/Alert';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  BOTTOM_BAR_OFFSET,
  ENV,
  PAGE_HEIGHT,
  SNACKBAR_DURATION,
} from '../constants';

import { post } from '../utils/fetch';
import validateNumber from '../utils/validateNumber';
import { formatPhoneForDisplay, normalizePhoneDigits } from '../utils/phoneInput';
import { useCurrentUser } from '../context/UserContext';

const styles = {
  loginButton: {
    width: '100%'
  },
}

export default function Login() {
  const { user, loading } = useCurrentUser();
  const navigate = useNavigate();
  const location = useLocation();
  const [phoneNumber, setPhoneNumber] = useState('');
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    type: '',
  });

  const { refetch: sendLoginUrl } = useQuery({
    queryKey: ['sendLoginRequest'],
    queryFn: async () => {
      const response = await post(`${ENV.API_URL}/sendLoginUrl`, {
        phoneNumber,
        referrer: location.state?.from?.pathname,
      });

      if (!response.ok) {
        throw new Error('Failed to send login code');
      }

      return response.json();
    },
    enabled: false,
  });

  const onChange = (e) => {
    setPhoneNumber(normalizePhoneDigits(e.target.value));
  };

  if (!loading && user) {
    navigate('/');
  }

  const handleSend = async () => {
    if (!phoneNumber || !validateNumber(phoneNumber)) {
      setSnackbar({
        open: true,
        message: 'Please enter a valid phone number!',
        type: 'error',
      });
      return;
    }

    const result = await sendLoginUrl();

    if (result.error) {
      setSnackbar({
        open: true,
        message: result.error?.message || 'Something happened...',
        type: 'error',
      });
      return;
    }

    if (result.data?.success) {
      navigate('/magic/code');
      return;
    }

    setSnackbar({
      open: true,
      message: 'Please enter a valid phone number!',
      type: 'error',
    });
  };

  return (
    <Box>
      <Grid
        container
        style={{
          minHeight: PAGE_HEIGHT,
          padding: `20px 22px ${BOTTOM_BAR_OFFSET}`,
        }}
        direction="column"
        justifyContent="center"
        alignItems="center"
      >
        <Grid item style={{ textAlign: 'center' }}>
          <Typography variant="h1" color="primary">
            Send Login Link
          </Typography>
          <TextField
            label="Enter Your Phone Number"
            name="phoneNumber"
            variant="outlined"
            margin="normal"
            value={formatPhoneForDisplay(phoneNumber)}
            onChange={onChange}
            inputProps={{
              inputMode: 'tel',
              autoComplete: 'tel-national',
            }}
            fullWidth
            required
          />
          <Button
            variant="contained"
            color="primary"
            disableElevation
            aria-label="Send Login Link"
            data-testid="login-send-button"
            fullWidth
            sx={styles.loginButton}
            onClick={handleSend}
          >
            Send
          </Button>
        </Grid>
      </Grid>
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        autoHideDuration={SNACKBAR_DURATION}
        open={snackbar.open}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity="error">{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}
